<template>
    <div class="temp-account-form">
        <template v-if="step === 'signup'">
            <p>{{ $t('pages.public.shared_records.temp_account_intro') }}</p>
            <div class="field-row">
                <input
                    v-model.trim="firstName"
                    type="text"
                    class="field"
                    :placeholder="$t('pages.public.shared_records.first_name')"
                />
                <input
                    v-model.trim="lastName"
                    type="text"
                    class="field"
                    :placeholder="$t('pages.public.shared_records.last_name')"
                />
            </div>
            <input
                v-model.trim="email"
                type="email"
                class="field"
                :readonly="!!recipientEmail"
                :placeholder="$t('pages.public.shared_records.email')"
            />
            <div class="field-row">
                <input v-model.trim="phoneCountryCode" type="text" class="field field--code" placeholder="+1" maxlength="4" />
                <input v-model.trim="phone" type="tel" class="field" :placeholder="$t('pages.public.shared_records.phone')" />
            </div>
            <input
                v-model="password"
                type="password"
                class="field"
                :placeholder="$t('pages.public.shared_records.password')"
                maxlength="40"
            />
            <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
            <common-buttons align="center">
                <common-button
                    :label="$t('pages.public.shared_records.create_account')"
                    type="primary"
                    :isProcessing="isSubmitting"
                    :isDisabled="!canSubmitSignup || isSubmitting"
                    @click="createAccount"
                ></common-button>
                <common-button
                    :label="$t('pages.public.shared_records.back')"
                    type="secondary"
                    @click="$emit('back')"
                ></common-button>
            </common-buttons>
        </template>

        <template v-else>
            <p>{{ $t('pages.public.shared_records.verify_email_hint', { email }) }}</p>
            <input
                v-model.trim="verificationCode"
                type="text"
                inputmode="numeric"
                maxlength="6"
                class="field"
                :placeholder="$t('pages.public.shared_records.verification_code')"
                @keydown.enter.prevent="verifyAndLogin"
            />
            <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
            <common-buttons align="center">
                <common-button
                    :label="$t('pages.public.shared_records.verify_and_continue')"
                    type="primary"
                    :isProcessing="isSubmitting"
                    :isDisabled="!isVerificationValid || isSubmitting"
                    @click="verifyAndLogin"
                ></common-button>
            </common-buttons>
        </template>
    </div>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import AuthAPI from 'api/AuthAPI'
    import UserRolesEnum from 'enums/UserRolesEnum'

    export default {
        props: {
            recipientEmail: { type: String, default: null },
        },

        data() {
            return {
                step: 'signup',
                firstName: '',
                lastName: '',
                email: this.recipientEmail || '',
                phone: '',
                phoneCountryCode: '+1',
                password: '',
                verificationCode: '',
                isSubmitting: false,
                errorMessage: '',
            }
        },

        computed: {
            canSubmitSignup() {
                return (
                    this.firstName &&
                    this.lastName &&
                    this.email &&
                    this.phone &&
                    this.phoneCountryCode &&
                    this.password &&
                    this.password.length >= 8
                )
            },

            isVerificationValid() {
                return /^\d{6}$/.test(this.verificationCode)
            },
        },

        methods: {
            createAccount() {
                if (!this.canSubmitSignup || this.isSubmitting) return

                this.isSubmitting = true
                this.errorMessage = ''

                return UsersAPI.create({
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email.trim().toLowerCase(),
                    phone: this.phone,
                    phoneCountryCode: this.phoneCountryCode,
                    password: this.password,
                    practiceType: 'physician',
                    practiceName: `${this.firstName} ${this.lastName}`.trim(),
                    userRoleId: UserRolesEnum.TEMPORARY_PHYSICIAN.ID,
                })
                    .then(() => {
                        this.step = 'verify'
                    })
                    .catch((err) => {
                        const fieldErrors = Array.isArray(err?.response?.data) ? err.response.data : []
                        if (fieldErrors.some((f) => f.field === 'email')) {
                            this.errorMessage = this.$t('pages.public.shared_records.errors.email_taken')
                        } else {
                            this.errorMessage = this.$t('pages.public.shared_records.errors.signup_failed')
                        }
                    })
                    .finally(() => {
                        this.isSubmitting = false
                    })
            },

            verifyAndLogin() {
                if (!this.isVerificationValid || this.isSubmitting) return

                this.isSubmitting = true
                this.errorMessage = ''

                return AuthAPI.verifyEmail({ email: this.email.trim().toLowerCase(), code: this.verificationCode })
                    .then(() => AuthAPI.logIn(this.email.trim().toLowerCase(), this.password))
                    .then((AuthenticationResult) => {
                        const { accessToken, idToken, refreshToken } = AuthenticationResult.signInUserSession
                        this.$auth.logIn(accessToken.jwtToken, idToken.jwtToken, refreshToken.token)
                        this.$emit('authenticated')
                    })
                    .catch(() => {
                        this.errorMessage = this.$t('pages.public.shared_records.errors.verify_failed')
                    })
                    .finally(() => {
                        this.isSubmitting = false
                    })
            },
        },
    }
</script>

<style scoped lang="scss">
    .temp-account-form {
        max-width: 420px;
        margin: 0 auto;
        text-align: left;
    }

    .field-row {
        display: flex;
        gap: 8px;
    }

    .field {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
        margin-bottom: 10px;
        box-sizing: border-box;

        &:focus {
            outline: none;
            border-color: #2e8b57;
        }

        &[readonly] {
            background: #f3f4f6;
        }

        &--code {
            max-width: 72px;
        }
    }

    .error {
        color: #c62828;
        margin-bottom: 12px;
        font-size: 13px;
    }
</style>
